import React from 'react';
import styles from './Checkbox.module.css'

interface Props {
    checked?: boolean;
    /**
     * Omit to render a plain indicator instead of a control.
     *
     * Some call sites already own the interaction and the semantics — `EntityMultiSelectRow` is a
     * `role="option"` with `aria-selected` and roving focus, and a real checkbox nested inside an
     * option is both invalid ARIA and a second tab stop for one choice. Those pass the state and
     * nothing else, and get an `aria-hidden` tick mark.
     */
    onChange?: (checked: boolean) => void;
    label?: string;
    disabled?: boolean;
    /** For an interactive checkbox whose visible label is rendered by the parent. */
    ariaLabel?: string;
}

const TickMark: React.FC<{ checked: boolean }> = ({ checked }) =>
    checked ? (
        <svg width="22" height="22" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 17C32 25.2843 25.2843 32 17 32C8.71573 32 2 25.2843 2 17C2 8.71573 8.71573 2 17 2C25.2843 2 32 8.71573 32 17Z" fill="#F6F6F6" stroke="currentColor" strokeWidth="3" />
            <path d="M12 17.0013L14.8047 19.806C15.0967 20.098 15.57 20.098 15.862 19.806L22 13.668" fill="#F6F6F6" />
            <path d="M12 17.0013L14.8047 19.806C15.0967 20.098 15.57 20.098 15.862 19.806L22 13.668" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ) : (
        <svg width="22" height="22" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 17C32 25.2843 25.2843 32 17 32C8.71573 32 2 25.2843 2 17C2 8.71573 8.71573 2 17 2C25.2843 2 32 8.71573 32 17Z" stroke="currentColor" strokeWidth="3" />
        </svg>
    );

/**
 * A real `<input type="checkbox">` under the tick mark, not a `<div onClick>`.
 *
 * The div this replaces had no `role` and no `tabIndex`, which made Ganztägig and "Als Serie
 * wiederholen" — two settings that reshape the whole Termin — reachable by mouse only. Going native
 * rather than bolting `role="checkbox"` onto the div buys Space-to-toggle, the checked state being
 * announced, participation in the form, and a focus ring, none of which then has to be maintained
 * here.
 *
 * The input is visually hidden rather than `display: none`: hidden that way it would stop being
 * focusable, which is the whole point. The `<label>` wrapper is what keeps the tick mark and the
 * text clickable.
 */
const Checkbox: React.FC<Props> = ({ checked = false, onChange, label, disabled, ariaLabel }) => {
    if (!onChange) {
        return (
            <span className={styles.main} aria-hidden="true">
                <TickMark checked={checked} />
                {label && <p className={styles.text}> {label} </p>}
            </span>
        );
    }

    return (
        <label className={`${styles.main} ${styles.interactive}`}>
            <input
                type="checkbox"
                className={styles.input}
                checked={checked}
                disabled={disabled}
                aria-label={label ? undefined : ariaLabel}
                onChange={(event) => onChange(event.target.checked)}
            />
            <span className={styles.box} aria-hidden="true">
                <TickMark checked={checked} />
            </span>
            {label && <p className={styles.text}> {label} </p>}
        </label>
    );
};

export default Checkbox;
